import React, { useEffect, useState } from 'react';

import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import classnames from 'classnames/bind';

import { notify } from '@NOTIFICATION/Notificator';

import COLOR from '@CONSTANTS/COLOR.constant';
import { GLOBAL_PERMISSIONS } from '@CONSTANTS/PERMISSIONS.constant';

import { AdminUser, useUpdateUserPermissions, useGetAllUsers } from '@SERVICES';

import useGlobalPermissions from '@HOOKS/useGlobalPermissions';
import useAuth from '@HOOKS/useAuth';

import UserHeader from '@PAGES/admin/users-permissions/components/UserHeader';
import UserInfo from '@PAGES/admin/users-permissions/components/UserInfo';
import Permissions from '@PAGES/admin/users-permissions/components/Permissions';
import Properties from '@PAGES/admin/users-permissions/components/Properties';
import BasicButton from '@COMPONENTS/COMMON/buttons/BasicButton';

import styles from './UserBody.module.scss';

const cx: CX = classnames.bind(styles);

function UserBody(props: Props) {
    const { user } = props;

    const {
        canManagePermissions,
    } = useGlobalPermissions();

    const [
        updatePermissions,
        {
            isLoading, isSuccess, isError, error,
        },
    ] = useUpdateUserPermissions();

    const { state: { user: currentUser } } = useAuth();
    const { data: usersData = { data: [] } } = useGetAllUsers();

    // Get current user's full data including isSuperAdmin and canAccessAppSettings
    const currentUserFullData = usersData.data.find((u) => u.id === currentUser?.id);

    // Determine if current user can edit the target user's permissions
    const canEditUserPermissions = (() => {
        // If current user is not an admin, they cannot edit any permissions
        if (!currentUserFullData?.isSuperuser && !currentUserFullData?.isSuperAdmin) {
            return false;
        }

        // If current user is admin but not super admin, they cannot edit super admin permissions
        if (currentUserFullData?.isSuperuser && !currentUserFullData?.isSuperAdmin) {
            if (user.isSuperAdmin) {
                return false;
            }
        }

        // Super admins can edit anyone's permissions
        if (currentUserFullData?.isSuperAdmin) {
            return true;
        }

        // Admins can edit regular users and other admins (but not super admins)
        if (currentUserFullData?.isSuperuser) {
            return !user.isSuperAdmin;
        }

        return false;
    })();

    const [isSuperuser, setIsSuperuser] = useState<boolean>(user.isSuperuser);
    const [canAccessAppSettings, setCanAccessAppSettings] = useState<boolean>(user.canAccessAppSettings);
    const [isSuperAdmin, setIsSuperAdmin] = useState<boolean>(user.isSuperAdmin);

    const [selectedPermissions, setSelectedPermissions] = useState<Set<GLOBAL_PERMISSIONS>>(
        new Set(user.permissions.map((p) => p.codename as GLOBAL_PERMISSIONS)),
    );

    const [isUserActive, setUserActive] = useState<boolean>(user.isActive);
    const [userExpirationDate, setUserExpirationDate] = useState<Date | null>(
        user.expirationDate ? new Date(user.expirationDate) : null,
    );

    useEffect(() => {
        if (isSuccess) {
            notify.info('Admin permissions have been updated', {
                toastId: 'update-user-permissions',
            });
        }
    }, [isSuccess]);

    useEffect(() => {
        if (isError) {
            if ((error as FetchBaseQueryError).status === 403) {
                notify.error('Permission denied', {
                    toastId: 'update-user-permissions-permission-error',
                });
            } else {
                notify.error('Something wrong!', {
                    toastId: 'update-user-permissions-error',
                });
            }
        }
    }, [isError, error]);

    return (
        <div className={cx('user-body')}>
            <UserHeader user={user} />
            
            <div className={cx('body-wrapper')}>
                <div className={cx('user-info-wrapper')}>
                    <UserInfo user={user} />
                </div>

                <div className={cx('user-options')}>
                    <div className={cx('permissions-wrapper')}>
                        <Permissions
                            selectedPermissions={selectedPermissions}
                            setSelectedPermissions={setSelectedPermissions}
                            isSuperuser={isSuperuser}
                            setIsSuperuser={setIsSuperuser}
                            canAccessAppSettings={canAccessAppSettings}
                            setCanAccessAppSettings={setCanAccessAppSettings}
                            isSuperAdmin={isSuperAdmin}
                            setIsSuperAdmin={setIsSuperAdmin}
                            user={user}
                        />
                    </div>

                    <div className={cx('properties-wrapper')}>
                        <Properties
                            isUserActive={isUserActive}
                            setUserActive={setUserActive}
                            userExpirationDate={userExpirationDate}
                            setUserExpirationDate={setUserExpirationDate}
                            user={user}
                        />
                    </div>

                    <div className={cx('button-wrapper')}>
                        <BasicButton
                            locked={!canManagePermissions || !canEditUserPermissions}
                            title="Save"
                            variant="success"
                            size="small"
                            isProcessing={isLoading}
                            style={{
                                height: 32,
                                width: '100%',
                                fontSize: 12,
                            }}
                            onClick={() => {
                                updatePermissions({
                                    id: user.id,
                                    isSuperuser,
                                    isActive: isUserActive,
                                    permissions: Array.from(selectedPermissions),
                                    expirationDate: userExpirationDate,
                                    canAccessAppSettings,
                                    isSuperAdmin,
                                });
                            }}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}

type Props = {
    user: AdminUser,
};

export default UserBody;
